import type {
  ConfirmedProspect,
  CrmDataset,
  CrmMetrics,
  Lead,
  Opportunity,
  Organization,
  PipelineStage,
} from '../types/crm'
import {
  CRM_DATA_START_ROW,
  CRM_HEADER_ROW,
  OPPORTUNITY_COLUMNS,
  ORGANIZATION_COLUMNS,
  cellFor,
  resolveColumns,
} from '../../netlify/functions/_shared/sheet-headers.mjs'

const PLACEHOLDERS = new Set([
  '',
  '-',
  'na',
  'n/a',
  'none',
  'not available',
  'not identified',
  'unknown',
])

export function cleanCell(value: unknown): string {
  return String(value ?? '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function isPlaceholder(value: unknown): boolean {
  return PLACEHOLDERS.has(cleanCell(value).toLowerCase())
}

export function normalizeName(value: unknown): string {
  if (isPlaceholder(value)) return ''
  return cleanCell(value)
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

export function parseMoney(value: unknown): number {
  const parsed = Number(cleanCell(value).replace(/[$,%\s]/g, '').replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

export function parseProbability(value: unknown): number {
  const text = cleanCell(value)
  if (!text) return 0
  const parsed = Number(text.replace('%', ''))
  if (!Number.isFinite(parsed)) return 0
  return text.includes('%') ? parsed / 100 : parsed > 1 ? parsed / 100 : parsed
}

/** Data rows with their real sheet row number; blank rows keep numbering intact. */
function dataRows(rows: unknown[][], columns: Partial<Record<string, number>>) {
  const indexes = Object.values(columns).filter((index): index is number => index !== undefined)
  return rows
    .slice(CRM_DATA_START_ROW - 1)
    .map((raw, offset) => ({ row: (raw ?? []).map(cleanCell), sheetRow: offset + CRM_DATA_START_ROW }))
    .filter(({ row }) => indexes.some((index) => row[index]))
}

// Columns are resolved from the row-4 headers, never fixed positions, so an
// inserted column (like Instagram Profile) cannot shift Email/Phone/City/State.
export function parseOrganizations(rows: unknown[][]): Organization[] {
  const columns = resolveColumns(rows[CRM_HEADER_ROW - 1], ORGANIZATION_COLUMNS, {
    sheet: 'Organizations',
    required: ['name'],
  })
  const read = (row: string[], field: keyof typeof ORGANIZATION_COLUMNS) => cleanCell(cellFor(row, columns, field))

  return dataRows(rows, columns).map(({ row, sheetRow }) => ({
    id: `org-${sheetRow}`,
    sheetRow,
    priority: read(row, 'priority'),
    name: read(row, 'name'),
    type: read(row, 'type'),
    accountManager: read(row, 'accountManager'),
    website: read(row, 'website'),
    facebook: read(row, 'facebook'),
    instagram: read(row, 'instagram'),
    linkedin: read(row, 'linkedin'),
    email: read(row, 'email'),
    phone: read(row, 'phone'),
    city: read(row, 'city'),
    state: read(row, 'state'),
    notes: read(row, 'notes'),
  }))
}

export function parseOpportunities(rows: unknown[][]): Opportunity[] {
  const columns = resolveColumns(rows[CRM_HEADER_ROW - 1], OPPORTUNITY_COLUMNS, {
    sheet: 'Opportunities',
    required: ['name', 'organization'],
  })
  const read = (row: string[], field: keyof typeof OPPORTUNITY_COLUMNS) => cleanCell(cellFor(row, columns, field))

  return dataRows(rows, columns).map(({ row, sheetRow }) => ({
    id: `opp-${sheetRow}`,
    sheetRow,
    priority: read(row, 'priority'),
    name: read(row, 'name'),
    dealOwner: read(row, 'dealOwner'),
    organization: read(row, 'organization'),
    status: read(row, 'status'),
    stage: read(row, 'stage'),
    lossReason: read(row, 'lossReason'),
    source: read(row, 'source'),
    value: parseMoney(read(row, 'value')),
    probability: parseProbability(read(row, 'probability')),
    expectedClose: read(row, 'expectedClose'),
    dateCollected: read(row, 'dateCollected'),
    notes: read(row, 'notes'),
  }))
}

function makeLead(
  organization: Organization | null,
  opportunity: Opportunity | null,
): Lead {
  const organizationName = organization?.name ?? opportunity?.organization ?? ''
  const ownerName = opportunity?.name ?? ''
  return {
    id: opportunity
      ? `lead-opportunity-${opportunity.sheetRow}`
      : `lead-organization-${organization?.sheetRow ?? 'unknown'}`,
    order: opportunity?.sheetRow ?? organization?.sheetRow ?? Number.MAX_SAFE_INTEGER,
    company: isPlaceholder(organizationName) ? 'Not Identified' : cleanCell(organizationName),
    owner: isPlaceholder(ownerName) ? 'Not Identified' : cleanCell(ownerName),
    ownerIdentified: !isPlaceholder(ownerName),
    priority: cleanCell(organization?.priority || opportunity?.priority) || ' - ',
    organization,
    opportunity,
  }
}

export function combineLeads(
  organizations: Organization[],
  opportunities: Opportunity[],
): Lead[] {
  const organizationsByName = new Map<string, Organization[]>()
  const organizationsByRow = new Map(organizations.map((organization) => [organization.sheetRow, organization]))
  const usedOrganizations = new Set<string>()

  for (const organization of organizations) {
    const key = normalizeName(organization.name)
    if (!key) continue
    const queue = organizationsByName.get(key) ?? []
    queue.push(organization)
    organizationsByName.set(key, queue)
  }

  const leads: Lead[] = []
  for (const opportunity of opportunities) {
    const key = normalizeName(opportunity.organization)
    let organization: Organization | null = null

    if (key) {
      const candidates = organizationsByName.get(key) ?? []
      organization =
        candidates.find((candidate) => candidate.sheetRow === opportunity.sheetRow)
        ?? candidates.find((candidate) => !usedOrganizations.has(candidate.id))
        ?? candidates[0]
        ?? null
    }

    if (!organization) {
      const sameRow = organizationsByRow.get(opportunity.sheetRow)
      if (sameRow && !usedOrganizations.has(sameRow.id)) organization = sameRow
    }

    if (organization) usedOrganizations.add(organization.id)
    leads.push(makeLead(organization, opportunity))
  }

  for (const organization of organizations) {
    if (!usedOrganizations.has(organization.id)) {
      leads.push(makeLead(organization, null))
    }
  }

  return [...leads].sort((a, b) => a.order - b.order)
}

function upsertSheetRecord<T extends { id: string; sheetRow: number }>(
  records: readonly T[],
  confirmedRecord: T,
): T[] {
  const matchIndex = records.findIndex(
    (record) => record.id === confirmedRecord.id || record.sheetRow === confirmedRecord.sheetRow,
  )
  const nextRecord = { ...confirmedRecord }

  if (matchIndex < 0) return [...records, nextRecord]
  return records.map((record, index) => index === matchIndex ? nextRecord : record)
}

export function mergeConfirmedProspect(
  dataset: CrmDataset,
  confirmed: ConfirmedProspect,
): CrmDataset {
  const organizations = upsertSheetRecord(dataset.organizations, confirmed.organization)
  const opportunities = upsertSheetRecord(dataset.opportunities, confirmed.opportunity)
  const leads = combineLeads(organizations, opportunities)

  return {
    organizations,
    opportunities,
    leads,
    metrics: calculateMetrics(organizations, opportunities, leads),
    loadedAt: dataset.loadedAt,
  }
}

export function calculateMetrics(
  organizations: Organization[],
  opportunities: Opportunity[],
  leads: Lead[],
): CrmMetrics {
  const stageMap = new Map<string, PipelineStage>()
  let openOpportunities = 0
  let pipelineValue = 0
  let weightedPipelineValue = 0

  for (const opportunity of opportunities) {
    const stage = cleanCell(opportunity.stage) || 'Unspecified'
    const existing = stageMap.get(stage) ?? { stage, count: 0, value: 0, weightedValue: 0 }
    existing.count += 1
    existing.value += opportunity.value
    existing.weightedValue += opportunity.value * opportunity.probability
    stageMap.set(stage, existing)

    if (cleanCell(opportunity.status).toLowerCase() === 'open') {
      openOpportunities += 1
      pipelineValue += opportunity.value
      weightedPipelineValue += opportunity.value * opportunity.probability
    }
  }

  const ownersIdentified = leads.filter((lead) => lead.ownerIdentified).length
  return {
    totalLeads: leads.length,
    organizations: organizations.length,
    opportunities: opportunities.length,
    ownersIdentified,
    missingOwners: leads.length - ownersIdentified,
    openOpportunities,
    pipelineValue,
    weightedPipelineValue,
    orphanOpportunities: leads.filter((lead) => !lead.organization && lead.opportunity).length,
    pipelineStages: [...stageMap.values()].sort((a, b) => b.value - a.value),
  }
}

export function createDataset(
  organizationRows: unknown[][],
  opportunityRows: unknown[][],
  loadedAt = new Date(),
): CrmDataset {
  const organizations = parseOrganizations(organizationRows)
  const opportunities = parseOpportunities(opportunityRows)
  const leads = combineLeads(organizations, opportunities)
  return {
    organizations,
    opportunities,
    leads,
    metrics: calculateMetrics(organizations, opportunities, leads),
    loadedAt,
  }
}
